import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Save, X } from 'lucide-react';

import { PageContainer } from '@/shared/components/layout/PageContainer';
import { AppButton } from '@/shared/components/ui/AppButton';
import { AppInput } from '@/shared/components/ui/AppInput';
import { AppSelect } from '@/shared/components/ui/AppSelect';
import { AppCard } from '@/shared/components/ui/AppCard';
import { BudgetInput } from '@/shared/components/promote/BudgetInput';
import { AdPlatform, CampaignStatus } from '@/shared/types/enums';
import type { CampaignObjective } from '@/shared/types/enums';
import { toDateInputValue, todayInputValue } from '@/shared/utils/date-input';
import { ROUTES } from '@/app/routes';

import { useCampaign } from '../../api/campaigns-queries';
import { useUpdateCampaign } from '../../api/campaigns-mutations';
import {
  CONVERSION_OBJECTIVES,
  META_CONVERSION_EVENTS,
} from '../../schemas/objective-budget.schema';
import { getTargetingCapabilities } from '../../logic/platform-constraints';
import { editCampaignSchema, type EditCampaignFormValues } from './edit-campaign.schema';
import styles from './CampaignEditPage.module.scss';

/**
 * Pull typed targeting fields out of the campaign's untyped `targetingConfig`
 * record. We tolerate missing / malformed values because early drafts may have
 * been created before the full wizard wrote every field.
 */
function extractTargeting(config: Record<string, unknown>): {
  ageMin: number;
  ageMax: number;
  genders: Array<'male' | 'female' | 'all'>;
  keywords: string;
} {
  const ageMin = typeof config.ageMin === 'number' ? config.ageMin : 18;
  const ageMax = typeof config.ageMax === 'number' ? config.ageMax : 65;
  const rawGenders = Array.isArray(config.genders) ? config.genders : [];
  const genders = rawGenders.filter(
    (g): g is 'male' | 'female' | 'all' => g === 'male' || g === 'female' || g === 'all',
  );
  const keywordsArr = Array.isArray(config.keywords) ? config.keywords : [];
  const keywords = keywordsArr.filter((k): k is string => typeof k === 'string').join(', ');
  return { ageMin, ageMax, genders, keywords };
}

function formatDateInput(iso: string | undefined): string {
  return toDateInputValue(iso) ?? '';
}

export function CampaignEditPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id ?? '';
  const navigate = useNavigate();

  const { data: campaign, isLoading, isError } = useCampaign(id);
  const updateMutation = useUpdateCampaign();

  const defaultValues = useMemo<EditCampaignFormValues | undefined>(() => {
    if (!campaign) return undefined;
    const targeting = extractTargeting(campaign.targetingConfig);
    return {
      name: campaign.name,
      startDate: formatDateInput(campaign.startDate),
      endDate: formatDateInput(campaign.endDate),
      dailyBudgetCents:
        campaign.budgetType === 'daily'
          ? campaign.budgetCents
          : Math.round(campaign.budgetCents / 7),
      totalBudgetCents:
        campaign.budgetType === 'total' ? campaign.budgetCents : campaign.budgetCents,
      ageMin: targeting.ageMin,
      ageMax: targeting.ageMax,
      genders: targeting.genders,
      keywords: targeting.keywords,
      pixelId: '',
      conversionEvent: 'PURCHASE',
      landingUrl: '',
    };
  }, [campaign]);

  const form = useForm<EditCampaignFormValues>({
    resolver: zodResolver(editCampaignSchema),
    defaultValues: defaultValues ?? {
      name: '',
      startDate: '',
      endDate: '',
      dailyBudgetCents: 0,
      totalBudgetCents: 0,
      ageMin: 18,
      ageMax: 65,
      genders: [],
      keywords: '',
      pixelId: '',
      conversionEvent: 'PURCHASE',
      landingUrl: '',
    },
  });
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = form;

  // Populate form once campaign loads (RHF defaultValues is a first-render
  // snapshot, so we manually reset when the async data arrives).
  useEffect(() => {
    if (defaultValues) reset(defaultValues);
  }, [defaultValues, reset]);

  const dailyBudgetCents = watch('dailyBudgetCents');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  // Auto-derive total budget from daily × schedule for daily-budget campaigns.
  // Total-budget campaigns keep their authoritative total (recalc would corrupt
  // it on save), so gate on budgetType.
  const budgetType = campaign?.budgetType;
  useEffect(() => {
    if (budgetType !== 'daily') return;
    if (dailyBudgetCents == null || startDate === '' || endDate === '') {
      setValue('totalBudgetCents', 0, { shouldDirty: false, shouldValidate: true });
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return;
    const msPerDay = 1000 * 60 * 60 * 24;
    const days = Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1;
    // shouldDirty:false so this derived write doesn't enable Save on mount.
    setValue('totalBudgetCents', dailyBudgetCents * days, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [budgetType, dailyBudgetCents, startDate, endDate, setValue]);

  const requiresConversionFields =
    campaign?.platform === AdPlatform.META &&
    CONVERSION_OBJECTIVES.includes(campaign?.objective as CampaignObjective);

  const targetingCaps = getTargetingCapabilities(campaign?.platform);

  const onSubmit = handleSubmit((values) => {
    const keywords = values.keywords
      ? values.keywords
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k.length > 0)
      : [];

    updateMutation.mutate(
      {
        id,
        payload: {
          name: values.name,
          // Sent as the bare `YYYY-MM-DD` the date input holds, matching what the
          // creation wizard posts. Converting to a full timestamp here made one
          // field arrive in two shapes depending on which screen wrote it, and
          // pinned `endDate` to midnight — the *start* of its last day.
          startDate: values.startDate,
          endDate: values.endDate,
          dailyBudgetCents: values.dailyBudgetCents,
          totalBudgetCents: values.totalBudgetCents,
          targeting: {
            ageMin: values.ageMin,
            ageMax: values.ageMax,
            genders: values.genders,
            // Backend update is partial: omitting keywords on platforms
            // without the concept leaves stored values untouched.
            ...(targetingCaps.keywords ? { keywords } : {}),
          },
          ...(requiresConversionFields && values.pixelId ? { pixelId: values.pixelId } : {}),
          ...(requiresConversionFields && values.conversionEvent
            ? { conversionEvent: values.conversionEvent }
            : {}),
          ...(requiresConversionFields && values.landingUrl
            ? { landingUrl: values.landingUrl }
            : {}),
        },
      },
      {
        onSuccess: () => {
          navigate({ to: '/promote/campaigns/$id', params: { id } });
        },
      },
    );
  });

  if (isLoading) {
    return (
      <PageContainer
        title="Edit campaign"
        breadcrumbs={[{ label: 'Campaigns', href: ROUTES.campaigns }, { label: 'Edit' }]}
      >
        <div className={styles.container}>
          <div className={styles.skeleton} style={{ height: 60 }} />
          <div className={styles.skeleton} style={{ height: 400 }} />
        </div>
      </PageContainer>
    );
  }

  if (isError || !campaign) {
    return (
      <PageContainer
        title="Campaign not found"
        breadcrumbs={[{ label: 'Campaigns', href: ROUTES.campaigns }, { label: 'Edit' }]}
      >
        <div className={styles.errorState}>
          <p>This campaign could not be found or you do not have access.</p>
          <AppButton variant="secondary" onClick={() => navigate({ to: ROUTES.campaigns })}>
            Back to campaigns
          </AppButton>
        </div>
      </PageContainer>
    );
  }

  // Non-draft campaigns can't be edited — show an explanation rather than a
  // broken form. We still allow viewing the field values so the user sees
  // what's deployed.
  if (campaign.status !== CampaignStatus.DRAFT) {
    return (
      <PageContainer
        title={campaign.name}
        breadcrumbs={[
          { label: 'Campaigns', href: ROUTES.campaigns },
          { label: campaign.name, href: `/promote/campaigns/${campaign.id}` },
          { label: 'Edit' },
        ]}
      >
        <div className={styles.container}>
          <AppCard className={styles.guardNotice}>
            <AlertTriangle size={18} aria-hidden />
            <div className={styles.guardContent}>
              <strong>This campaign can&apos;t be edited</strong>
              <p>
                Only draft campaigns can be edited. Once a campaign has been launched on the ad
                platform, changes must go through pause + resubmit.
              </p>
            </div>
          </AppCard>
          <div className={styles.actions}>
            <AppButton
              variant="secondary"
              onClick={() =>
                navigate({ to: '/promote/campaigns/$id', params: { id: campaign.id } })
              }
            >
              Back to campaign
            </AppButton>
          </div>
        </div>
      </PageContainer>
    );
  }

  const genderOptions: Array<{ value: 'male' | 'female' | 'all'; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
  ];

  return (
    <PageContainer
      title="Edit campaign"
      subtitle={campaign.name}
      breadcrumbs={[
        { label: 'Campaigns', href: ROUTES.campaigns },
        { label: campaign.name, href: `/promote/campaigns/${campaign.id}` },
        { label: 'Edit' },
      ]}
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Campaign info</h2>
          <AppInput
            label="Campaign name"
            placeholder="e.g., Summer Music Festival 2026 — Ticket Push"
            error={errors.name?.message}
            {...register('name')}
          />
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Schedule</h2>
          <div className={styles.row}>
            <AppInput
              label="Start date"
              type="date"
              min={todayInputValue()}
              error={errors.startDate?.message}
              {...register('startDate')}
            />
            <AppInput
              label="End date"
              type="date"
              min={startDate || todayInputValue()}
              error={errors.endDate?.message}
              {...register('endDate')}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Budget</h2>
          <p className={styles.sectionDescription}>
            The total is recalculated from the daily budget and schedule.
          </p>
          <div className={styles.row}>
            <Controller
              control={control}
              name="dailyBudgetCents"
              render={({ field }) => (
                <BudgetInput
                  label="Daily budget"
                  currency={campaign.currency}
                  value={Number.isFinite(field.value) ? (field.value / 100).toFixed(2) : ''}
                  onChange={(cents) => field.onChange(cents)}
                  error={errors.dailyBudgetCents?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="totalBudgetCents"
              render={({ field }) => (
                <BudgetInput
                  label="Total / lifetime budget"
                  currency={campaign.currency}
                  value={Number.isFinite(field.value) ? (field.value / 100).toFixed(2) : ''}
                  onChange={(cents) => field.onChange(cents)}
                  error={errors.totalBudgetCents?.message}
                  helperText="Calculated automatically"
                  readOnly
                />
              )}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Audience targeting</h2>
          <p className={styles.sectionDescription}>
            Coarse controls — adjust age
            {targetingCaps.keywords ? ', gender, and keywords' : ' and gender'}. For deeper
            targeting (locations, interests) use the wizard.
          </p>
          <div className={styles.row}>
            <AppInput
              label="Min age"
              type="number"
              min={13}
              max={65}
              error={errors.ageMin?.message}
              {...register('ageMin', { valueAsNumber: true })}
            />
            <AppInput
              label="Max age"
              type="number"
              min={13}
              max={65}
              error={errors.ageMax?.message}
              {...register('ageMax', { valueAsNumber: true })}
            />
          </div>
          <Controller
            control={control}
            name="genders"
            render={({ field }) => (
              <div className={styles.gendersRow} role="group" aria-label="Genders">
                {genderOptions.map((opt) => {
                  const active = field.value.includes(opt.value);
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      className={
                        active
                          ? `${styles.genderPill} ${styles.genderPillActive}`
                          : styles.genderPill
                      }
                      onClick={() => {
                        if (active) {
                          field.onChange(field.value.filter((g) => g !== opt.value));
                        } else {
                          field.onChange([...field.value, opt.value]);
                        }
                      }}
                      aria-pressed={active}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          />
          {targetingCaps.keywords && (
            <AppInput
              label="Keywords"
              placeholder="comma, separated, terms"
              helperText="Comma-separated list of keywords the ad platform can use."
              {...register('keywords')}
            />
          )}
        </div>

        {requiresConversionFields && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Conversion tracking</h2>
            <p className={styles.sectionDescription}>
              Meta optimizes delivery towards this Pixel event. Leave the Pixel field blank to keep
              the current one.
            </p>
            <div className={styles.row}>
              <AppInput
                label="Meta Pixel ID"
                placeholder="Paste new Pixel ID to replace the current one"
                error={errors.pixelId?.message}
                {...register('pixelId')}
              />
              <Controller
                control={control}
                name="conversionEvent"
                render={({ field }) => (
                  <AppSelect
                    label="Conversion event"
                    placeholder="Select an event…"
                    options={META_CONVERSION_EVENTS.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    value={field.value ?? 'PURCHASE'}
                    onValueChange={field.onChange}
                    error={errors.conversionEvent?.message}
                  />
                )}
              />
            </div>
            <AppInput
              label="Landing URL"
              placeholder="https://events.com/shows/..."
              error={errors.landingUrl?.message}
              {...register('landingUrl')}
            />
          </div>
        )}

        <div className={styles.actions}>
          <AppButton
            type="button"
            variant="ghost"
            onClick={() => navigate({ to: '/promote/campaigns/$id', params: { id: campaign.id } })}
          >
            <X size={14} />
            Cancel
          </AppButton>
          <AppButton type="submit" loading={updateMutation.isPending} disabled={!isDirty}>
            <Save size={14} />
            Save changes
          </AppButton>
        </div>
      </form>
    </PageContainer>
  );
}
